// Typed Vendor AI client (APP-003 R1 P5). Mirrors the Vendor AI OpenAPI
// contract for the surfaces the Studio module uses. Every call goes through the
// Studio Worker proxy (`/organizations/{orgId}/vendor-ai/...`), which is the
// authentication, entitlement and per-user permission boundary; the browser
// never talks to the Vendor AI backend directly and never holds a credential
// for it.
import { ApiError } from "./types";

export interface VendorResponse<T> {
  data: T;
  demoMode: boolean;
  correlationId: string | null;
}

export interface VendorClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export type RiskTier = "TIER_1" | "TIER_2" | "TIER_3" | "TIER_4";
export type Criticality = "CRITICAL" | "HIGH" | "MODERATE" | "LOW";
export type LifecycleState =
  | "DRAFT"
  | "IN_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "ACTIVE"
  | "SUSPENDED"
  | "OFFBOARDING"
  | "OFFBOARDED";
/** Derived from the lifecycle state by the database. Read-only. */
export type VendorStatus = "pending" | "active" | "restricted" | "inactive";
export type IntakeStatus = "PENDING" | "APPROVED" | "REJECTED" | "WITHDRAWN";
export type EvidenceState = "CURRENT" | "SUPERSEDED" | "WITHDRAWN";
/** Derived on every read from the server's date; never stored. */
export type EvidenceValidity =
  | "VALID"
  | "EXPIRING"
  | "EXPIRED"
  | "NOT_YET_EFFECTIVE"
  | "SUPERSEDED"
  | "WITHDRAWN";
export type VerificationOutcome = "MATCH" | "MISMATCH" | "UNAVAILABLE";

export interface VendorCategory {
  id: string;
  organizationId: string | null;
  key: string;
  label: string;
}

export interface VendorOwner {
  id: string;
  vendorId: string;
  ownerUserId: string;
  ownerRole: string;
  assignedBy: string;
  assignedAt: string;
  unassignedAt: string | null;
}

export interface Vendor {
  id: string;
  organizationId: string;
  legalName: string;
  tradingName: string | null;
  website: string | null;
  externalIdentifier: string | null;
  categoryId: string;
  categoryKey: string;
  categoryLabel: string;
  riskTier: RiskTier;
  criticality: Criticality;
  dataClassifications: string[];
  countriesOfOperation: string[];
  dataResidenceCountries: string[];
  annualValueBand: string | null;
  currency: string | null;
  lifecycleState: LifecycleState;
  status: VendorStatus;
  owner: VendorOwner | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface VendorClassification {
  id: string;
  vendorId: string;
  revision: number;
  riskTier: RiskTier;
  criticality: Criticality;
  dataClassifications: string[];
  countriesOfOperation: string[];
  dataResidenceCountries: string[];
  rationale: string;
  changedBy: string;
  createdAt: string;
}

export interface VendorIntakeRequest {
  id: string;
  organizationId: string;
  vendorId: string;
  requestedBy: string;
  businessJustification: string;
  intendedUse: string;
  dataShared: string[];
  requestedTier: RiskTier | null;
  status: IntakeStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  /** Quote back as expectedVersion so a decision from a stale screen is refused. */
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface VendorIntakeDecision {
  id: string;
  requestId: string;
  vendorId: string;
  decision: "SUBMITTED" | "APPROVED" | "REJECTED" | "WITHDRAWN";
  actorId: string;
  reason: string;
  createdAt: string;
}

export interface VendorLifecycleTransition {
  id: string;
  vendorId: string;
  fromState: LifecycleState;
  toState: LifecycleState;
  actorId: string;
  reason: string;
  intakeRequestId: string | null;
  createdAt: string;
}

export interface VendorDashboard {
  totalVendors: number;
  byRiskTier: Record<string, number>;
  byCriticality: Record<string, number>;
  byLifecycleState: Record<string, number>;
  unowned: number;
  withRegulatedData: number;
}

export interface VendorEvidence {
  id: string;
  organizationId: string;
  vendorId: string;
  evidenceType: string;
  evidenceTypeLabel: string;
  documentId: string;
  documentVersion: number;
  /** What Studio Documents reported when the evidence was attached. */
  documentChecksum: string;
  documentFilename: string;
  documentMediaType: string;
  documentByteSize: number;
  issuedBy: string | null;
  notes: string;
  effectiveFrom: string;
  expiresAt: string | null;
  attachedBy: string;
  state: EvidenceState;
  validity: EvidenceValidity;
  daysUntilExpiry: number | null;
  supersedesId: string | null;
  supersededById: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closedReason: string | null;
  lastVerifiedAt: string | null;
  lastVerification: VerificationOutcome | null;
  createdAt: string;
}

export interface VendorEvidenceVerification {
  id: string;
  evidenceId: string;
  vendorId: string;
  outcome: VerificationOutcome;
  expectedChecksum: string;
  observedChecksum: string | null;
  detail: string;
  actorId: string;
  createdAt: string;
}

export interface EvidenceCoverageRequirement {
  evidenceType: string;
  evidenceTypeLabel: string;
  status: "SATISFIED" | "EXPIRING" | "EXPIRED" | "MISSING";
  evidenceId: string | null;
  expiresAt: string | null;
}

export interface EvidenceCoverage {
  vendorId: string;
  riskTier: RiskTier;
  satisfied: number;
  outstanding: number;
  required: EvidenceCoverageRequirement[];
}

export interface CreateVendorInput {
  legalName: string;
  tradingName?: string | null;
  website?: string | null;
  externalIdentifier?: string | null;
  categoryKey: string;
  riskTier: RiskTier;
  criticality: Criticality;
  dataClassifications?: string[];
  countriesOfOperation?: string[];
  dataResidenceCountries?: string[];
  annualValueBand?: string | null;
  currency?: string | null;
  ownerUserId?: string | null;
}

export interface ClassifyInput {
  riskTier: RiskTier;
  criticality: Criticality;
  dataClassifications?: string[];
  countriesOfOperation?: string[];
  dataResidenceCountries?: string[];
  rationale?: string;
}

export interface SubmitIntakeInput {
  businessJustification: string;
  intendedUse: string;
  dataShared?: string[];
  requestedTier?: RiskTier | null;
}

export interface AttachEvidenceInput {
  evidenceType: string;
  documentId: string;
  effectiveFrom: string;
  expiresAt?: string | null;
  issuedBy?: string | null;
  notes?: string;
  supersedesId?: string | null;
}

export interface VendorFilter {
  riskTier?: RiskTier;
  criticality?: Criticality;
  lifecycleState?: LifecycleState;
  ownerUserId?: string;
  dataClassification?: string;
  categoryKey?: string;
  search?: string;
}

// ----- R2: assessments, proposals and findings ------------------------------

export type QuestionType = "BOOLEAN" | "SINGLE_SELECT" | "MULTI_SELECT" | "TEXT" | "EVIDENCE_REQUEST";
export type TemplateVersionStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
export type AssessmentStatus = "OPEN" | "SUBMITTED" | "CANCELLED";
export type ProposalOutcome = "PROPOSED" | "UNSUPPORTED" | "CONTRADICTED";
export type ProposalDecision = "PENDING" | "ACCEPTED" | "EDITED" | "REJECTED";
export type VendorFindingSeverity = "CRITICAL" | "HIGH" | "MODERATE" | "LOW";
export type VendorFindingState = "OPEN" | "IN_PROGRESS" | "ACCEPTED_RISK" | "RESOLVED";
/** Shaped by the question type: boolean, string, or a list of strings. */
export type AnswerValue = boolean | string | string[];

export interface AssessmentTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  status: TemplateVersionStatus;
  notes: string;
  createdBy: string | null;
  createdAt: string;
  publishedBy?: string | null;
  publishedAt?: string | null;
  archivedBy?: string | null;
  archivedAt?: string | null;
  questionCount: number;
}

export interface AssessmentTemplate {
  id: string;
  /** Null for the templates Odingard ships, which no organization may edit. */
  organizationId: string | null;
  key: string;
  label: string;
  description: string;
  createdBy: string | null;
  createdAt: string;
  versions: AssessmentTemplateVersion[];
}

export interface AssessmentQuestion {
  id: string;
  sectionId: string;
  position: number;
  key: string;
  prompt: string;
  guidance: string;
  questionType: QuestionType;
  options: string[];
  required: boolean;
  evidenceType?: string | null;
  /** A display reference such as CC6.1. Policy AI owns controls; this is not a join. */
  controlReference?: string | null;
}

export interface AssessmentSection {
  id: string;
  versionId: string;
  position: number;
  key: string;
  title: string;
  guidance: string;
  questions: AssessmentQuestion[];
}

export interface AssessmentAnswer {
  id: string;
  questionId: string;
  value: AnswerValue;
  rationale: string;
  evidenceId: string | null;
  answeredBy: string;
  answeredAt: string;
  revision: number;
}

export interface Assessment {
  id: string;
  organizationId: string;
  vendorId: string;
  /** Pinned at creation, so answers stay readable against the questions asked. */
  templateVersionId: string;
  title: string;
  status: AssessmentStatus;
  assignedTo: string | null;
  dueOn: string | null;
  openedBy: string;
  openedAt: string;
  submittedBy: string | null;
  submittedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  /** Counted from the answers, never stored. */
  answeredCount: number;
  /** Required questions still unanswered. Submission is refused above zero. */
  requiredOutstanding: number;
}

export interface AssessmentDetail extends Assessment {
  sections: AssessmentSection[];
  answers: AssessmentAnswer[];
}

export interface AnswerProposalCitation {
  evidenceId: string;
  documentId: string;
  documentVersion: number;
  documentChecksum: string;
  excerpt: string;
}

/**
 * A machine's suggestion. It is derived, never authoritative: it becomes an
 * answer only when a named person accepts it, and an UNSUPPORTED or
 * CONTRADICTED outcome carries no value at all.
 */
export interface AnswerProposal {
  id: string;
  assessmentId: string;
  questionId: string;
  outcome: ProposalOutcome;
  decision: ProposalDecision;
  value: AnswerValue | null;
  rationale: string;
  refusalReason: string | null;
  confidence: number;
  model: string;
  promptVersion: string;
  requestedBy: string;
  generatedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string;
  answerId: string | null;
  citations: AnswerProposalCitation[];
}

export interface VendorFindingTransition {
  id: string;
  kind: "STATE" | "ASSIGNMENT";
  fromState: VendorFindingState | null;
  toState: VendorFindingState | null;
  fromOwner: string | null;
  toOwner: string | null;
  fromDueOn: string | null;
  toDueOn: string | null;
  reason: string;
  actorId: string;
  occurredAt: string;
}

export interface VendorFinding {
  id: string;
  vendorId: string;
  assessmentId: string | null;
  questionId: string | null;
  title: string;
  description: string;
  severity: VendorFindingSeverity;
  state: VendorFindingState;
  ownerUserId: string;
  dueOn: string;
  raisedBy: string;
  raisedAt: string;
  closedBy: string | null;
  closedAt: string | null;
  closureNote: string;
  updatedAt: string;
  /** Derived on every read from the server's date, never stored. */
  overdue: boolean;
  history: VendorFindingTransition[];
}

export interface OpenAssessmentInput {
  templateVersionId: string;
  title?: string | null;
  assignedTo?: string | null;
  dueOn?: string | null;
}

export interface AnswerQuestionInput {
  value: AnswerValue;
  rationale?: string;
  evidenceId?: string | null;
}

export interface DecideProposalInput {
  decision: "ACCEPTED" | "EDITED" | "REJECTED";
  /** The answer the person is giving instead. Required for EDITED. */
  value?: AnswerValue | null;
  note?: string;
}

export interface RaiseVendorFindingInput {
  vendorId: string;
  assessmentId?: string | null;
  questionId?: string | null;
  title: string;
  description?: string;
  severity: VendorFindingSeverity;
  ownerUserId: string;
  dueOn: string;
}

export interface VendorFindingFilter {
  vendorId?: string;
  assessmentId?: string;
  state?: VendorFindingState;
  ownerUserId?: string;
  severity?: VendorFindingSeverity;
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function raise(status: number, data: unknown): never {
  const err = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)
    ?.error;
  throw new ApiError(
    status,
    err?.code ?? "error",
    err?.message ?? "Vendor AI could not complete that request.",
    err?.details,
  );
}

function query(params: Record<string, string | undefined | never>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) q.set(key, value);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export class VendorAiClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: VendorClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  private async request<T>(
    method: string,
    orgId: string,
    subpath: string,
    body?: unknown,
  ): Promise<VendorResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const path = `/organizations/${orgId}/vendor-ai/${subpath.replace(/^\/+/, "")}`;
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const data = await parse(res);
    if (!res.ok) raise(res.status, data);
    return {
      data: data as T,
      demoMode: res.headers.get("x-vendor-ai-mode") === "demo",
      correlationId: res.headers.get("x-correlation-id"),
    };
  }

  // ----- Register -----------------------------------------------------------
  dashboard(orgId: string) {
    return this.request<{ dashboard: VendorDashboard }>("GET", orgId, "vendor-dashboard");
  }
  categories(orgId: string) {
    return this.request<{ categories: VendorCategory[] }>("GET", orgId, "vendor-categories");
  }
  vendors(orgId: string, filter: VendorFilter = {}) {
    return this.request<{ vendors: Vendor[] }>("GET", orgId, `vendors${query({ ...filter })}`);
  }
  vendor(orgId: string, vendorId: string) {
    return this.request<{ vendor: Vendor }>("GET", orgId, `vendors/${vendorId}`);
  }
  createVendor(orgId: string, input: CreateVendorInput) {
    return this.request<{ vendor: Vendor }>("POST", orgId, "vendors", input);
  }
  assignOwner(orgId: string, vendorId: string, ownerUserId: string, ownerRole?: string) {
    return this.request<{ vendor: Vendor }>("PUT", orgId, `vendors/${vendorId}/owner`, {
      ownerUserId,
      ...(ownerRole ? { ownerRole } : {}),
    });
  }
  owners(orgId: string, vendorId: string) {
    return this.request<{ owners: VendorOwner[] }>("GET", orgId, `vendors/${vendorId}/owners`);
  }
  classify(orgId: string, vendorId: string, input: ClassifyInput) {
    return this.request<{ vendor: Vendor }>("PUT", orgId, `vendors/${vendorId}/classification`, input);
  }
  classifications(orgId: string, vendorId: string) {
    return this.request<{ classifications: VendorClassification[] }>(
      "GET",
      orgId,
      `vendors/${vendorId}/classifications`,
    );
  }

  // ----- Governed intake ----------------------------------------------------
  submitIntake(orgId: string, vendorId: string, input: SubmitIntakeInput) {
    return this.request<{ request: VendorIntakeRequest }>(
      "POST",
      orgId,
      `vendors/${vendorId}/intake-requests`,
      input,
    );
  }
  intakeRequests(orgId: string, filter: { status?: IntakeStatus; vendorId?: string } = {}) {
    return this.request<{ requests: VendorIntakeRequest[] }>(
      "GET",
      orgId,
      `vendor-intake-requests${query(filter)}`,
    );
  }
  intakeRequest(orgId: string, requestId: string) {
    return this.request<{ request: VendorIntakeRequest }>("GET", orgId, `vendor-intake-requests/${requestId}`);
  }
  /**
   * Decide a pending request. `expectedVersion` is the version the screen read,
   * so a decision taken against a stale view is refused rather than applied.
   */
  decideIntake(
    orgId: string,
    requestId: string,
    input: { decision: "APPROVED" | "REJECTED"; reason: string; expectedVersion?: number },
  ) {
    return this.request<{ request: VendorIntakeRequest }>(
      "POST",
      orgId,
      `vendor-intake-requests/${requestId}/decision`,
      input,
    );
  }
  withdrawIntake(orgId: string, requestId: string, reason: string) {
    return this.request<{ request: VendorIntakeRequest }>(
      "POST",
      orgId,
      `vendor-intake-requests/${requestId}/withdrawal`,
      { reason },
    );
  }
  intakeDecisions(orgId: string, requestId: string) {
    return this.request<{ decisions: VendorIntakeDecision[] }>(
      "GET",
      orgId,
      `vendor-intake-requests/${requestId}/decisions`,
    );
  }
  transitions(orgId: string, vendorId: string) {
    return this.request<{ transitions: VendorLifecycleTransition[] }>(
      "GET",
      orgId,
      `vendors/${vendorId}/transitions`,
    );
  }
  transition(orgId: string, vendorId: string, toState: LifecycleState, reason: string) {
    return this.request<{ vendor: Vendor; transitions: VendorLifecycleTransition[] }>(
      "POST",
      orgId,
      `vendors/${vendorId}/transitions`,
      { toState, reason },
    );
  }

  // ----- Evidence -----------------------------------------------------------
  vendorEvidence(orgId: string, vendorId: string) {
    return this.request<{ evidence: VendorEvidence[] }>("GET", orgId, `vendors/${vendorId}/evidence`);
  }
  coverage(orgId: string, vendorId: string) {
    return this.request<{ coverage: EvidenceCoverage }>("GET", orgId, `vendors/${vendorId}/evidence/coverage`);
  }
  attachEvidence(orgId: string, vendorId: string, input: AttachEvidenceInput) {
    return this.request<{ evidence: VendorEvidence }>("POST", orgId, `vendors/${vendorId}/evidence`, input);
  }
  evidenceRecord(orgId: string, evidenceId: string) {
    return this.request<{ evidence: VendorEvidence }>("GET", orgId, `vendor-evidence/${evidenceId}`);
  }
  verifications(orgId: string, evidenceId: string) {
    return this.request<{ verifications: VendorEvidenceVerification[] }>(
      "GET",
      orgId,
      `vendor-evidence/${evidenceId}/verifications`,
    );
  }
  /**
   * Re-read the referenced document and compare its checksum with the one the
   * evidence relied on. A MISMATCH is appended as a finding; the recorded
   * checksum is never repointed at the new document.
   */
  verifyEvidence(orgId: string, evidenceId: string) {
    return this.request<{ evidence: VendorEvidence; outcome: VendorEvidenceVerification }>(
      "POST",
      orgId,
      `vendor-evidence/${evidenceId}/verification`,
      {},
    );
  }
  withdrawEvidence(orgId: string, evidenceId: string, reason: string) {
    return this.request<{ evidence: VendorEvidence }>(
      "POST",
      orgId,
      `vendor-evidence/${evidenceId}/withdrawal`,
      { reason },
    );
  }

  // ----- Assessments (R2) ---------------------------------------------------
  templates(orgId: string) {
    return this.request<{ templates: AssessmentTemplate[] }>("GET", orgId, "vendor-assessment-templates");
  }
  assessments(orgId: string, filter: { vendorId?: string; status?: AssessmentStatus; assignedTo?: string } = {}) {
    return this.request<{ assessments: Assessment[] }>("GET", orgId, `vendor-assessments${query(filter)}`);
  }
  assessment(orgId: string, assessmentId: string) {
    return this.request<{ assessment: AssessmentDetail }>("GET", orgId, `vendor-assessments/${assessmentId}`);
  }
  /** Pins a published template version to the vendor. One open at a time. */
  openAssessment(orgId: string, vendorId: string, input: OpenAssessmentInput) {
    return this.request<{ assessment: AssessmentDetail }>(
      "POST",
      orgId,
      `vendors/${vendorId}/assessments`,
      input,
    );
  }
  assignAssessment(orgId: string, assessmentId: string, input: { assignedTo?: string | null; dueOn?: string | null }) {
    return this.request<{ assessment: Assessment }>(
      "PUT",
      orgId,
      `vendor-assessments/${assessmentId}/assignment`,
      input,
    );
  }
  answerQuestion(orgId: string, assessmentId: string, questionId: string, input: AnswerQuestionInput) {
    return this.request<{ assessment: AssessmentDetail }>(
      "PUT",
      orgId,
      `vendor-assessments/${assessmentId}/answers/${questionId}`,
      input,
    );
  }
  answerRevisions(orgId: string, assessmentId: string, questionId: string) {
    return this.request<{ revisions: AssessmentAnswer[] }>(
      "GET",
      orgId,
      `vendor-assessments/${assessmentId}/answers/${questionId}/revisions`,
    );
  }
  submitAssessment(orgId: string, assessmentId: string) {
    return this.request<{ assessment: Assessment }>(
      "POST",
      orgId,
      `vendor-assessments/${assessmentId}/submission`,
      {},
    );
  }
  cancelAssessment(orgId: string, assessmentId: string, reason: string) {
    return this.request<{ assessment: Assessment }>(
      "POST",
      orgId,
      `vendor-assessments/${assessmentId}/cancellation`,
      { reason },
    );
  }

  // ----- AI answer proposals (R2) -------------------------------------------
  proposals(orgId: string, assessmentId: string) {
    return this.request<{ proposals: AnswerProposal[] }>(
      "GET",
      orgId,
      `vendor-assessments/${assessmentId}/proposals`,
    );
  }
  /** Asks for an answer to be drafted from the vendor's evidence. */
  proposeAnswer(orgId: string, assessmentId: string, questionId: string) {
    return this.request<{ proposal: AnswerProposal }>(
      "POST",
      orgId,
      `vendor-assessments/${assessmentId}/proposals`,
      { questionId },
    );
  }
  /**
   * A person decides. Accepting writes an ordinary answer under their name; a
   * refusal (UNSUPPORTED, CONTRADICTED) can only be rejected or answered by hand.
   */
  decideProposal(orgId: string, assessmentId: string, proposalId: string, input: DecideProposalInput) {
    return this.request<{ assessment: AssessmentDetail }>(
      "POST",
      orgId,
      `vendor-assessments/${assessmentId}/proposals/${proposalId}/decision`,
      input,
    );
  }

  // ----- Findings (R2) ------------------------------------------------------
  findings(orgId: string, filter: VendorFindingFilter = {}) {
    return this.request<{ findings: VendorFinding[] }>("GET", orgId, `vendor-findings${query({ ...filter })}`);
  }
  finding(orgId: string, findingId: string) {
    return this.request<{ finding: VendorFinding }>("GET", orgId, `vendor-findings/${findingId}`);
  }
  raiseFinding(orgId: string, input: RaiseVendorFindingInput) {
    return this.request<{ finding: VendorFinding }>("POST", orgId, "vendor-findings", input);
  }
  /** Progress or close. ACCEPTED_RISK is an approval and has its own route. */
  transitionFinding(orgId: string, findingId: string, state: "IN_PROGRESS" | "RESOLVED", note: string) {
    return this.request<{ finding: VendorFinding }>("POST", orgId, `vendor-findings/${findingId}/transitions`, {
      state,
      note,
    });
  }
  acceptFindingRisk(orgId: string, findingId: string, note: string) {
    return this.request<{ finding: VendorFinding }>("POST", orgId, `vendor-findings/${findingId}/risk-acceptance`, {
      note,
    });
  }
  reassignFinding(orgId: string, findingId: string, input: { ownerUserId: string; dueOn: string; reason?: string }) {
    return this.request<{ finding: VendorFinding }>("POST", orgId, `vendor-findings/${findingId}/assignment`, input);
  }
}
